import React from "react";
import moment from "moment";

import { Icon, Popup } from 'semantic-ui-react'

import { getText } from "../../helpers";
import { checkPendingTranslation } from "../../helpers/checkPendingTranslation";
import ControlLibraryActions from "./control_library_actions";
import TagsDropdown from "../../components/tags_dropdown";
import LinkifiedText from "../../components/linkified_text";
import ControlTranslationIdLabel from "../../components/control_translation_id_label";

function LibraryControlComponent(props) {
  const { control, onClick, showTranslationModal, instanceConfig, isPendingTranslation, isFailedTranslation } = props;
  const { features } = instanceConfig || {};
  const isEnabledTranslations = features && features.ai_translation
  const selectedLanguage = localStorage.getItem('language') || "english"
  const translation = isEnabledTranslations && control.translations && control.translations[selectedLanguage.toLowerCase()]
  const translation_result = translation && translation.translation_result

  if (translation_result) {
    translation_result.id = control.id;
    translation_result.custom_id = control.custom_id;
  }

  const controlDetails = translation_result || control

  function onTranslationIdClick() {
    if (showTranslationModal && translation) {
      showTranslationModal(translation.id)
    }
  }

  return (
    <div className="library-control-component">
      <div className="control-details">
        {isPendingTranslation && (
          <div className="pending-translation">
            {getText("Translating")} <div className="ui active inline loader"></div>
          </div>
        )}
        {isFailedTranslation && (
          <div className="failed-translation">
            {getText('Translation failed. Try again by clicking "New Translation" in Translations Table.')}
          </div>
        )}
        <span className="cursor-pointer">
          <ControlTranslationIdLabel translation={translation} onClick={onTranslationIdClick} />
        </span>
        {translation && <br />}
        {onClick && (
          <span className="dynamic-custom-id link" onClick={() => onClick(control)}>
            {control.custom_id}
          </span>
        )}
        {!onClick && <span className="static-custom-id">{control.custom_id}</span>}
        <span className="control-title">{controlDetails.title}</span>
        <span> / </span>
        <span className="control-subtitle">{controlDetails.subtitle}</span>
      </div>
      <div className="control-description">
        <LinkifiedText text={controlDetails.description} />
      </div>
      <div className="control-ids">
        <span className="control-id">
          <label>{getText("System Id").toUpperCase()}:</label> {control.id}
        </span>
        <span> / </span>
        <span className="custom-id">
          <label>{getText("Custom Id").toUpperCase()}:</label> {control.custom_id}
        </span>
      </div>
      <TagsAndOwnersViewComponent control={control} removeTag={props.removeTag} removeOwner={props.removeOwner} saveNow={false} />
      {control.archived && <div className="archived archived-label">{getText("Archived")} ({moment(control.archive_date).format("DD-MMM-YYYY")})</div>}
    </div>
  );
}

function TagsAndOwnersViewComponent(props) {
  return (
    <div className="library-control-tags-and-owners">
      <OwnersViewComponent control={props.control} removeOwner={props.removeOwner} saveNow={true} />
      <TagsViewComponent control={props.control} removeTag={props.removeTag} saveNow={true} />
    </div>
  );
}

function OwnersViewComponent(props) {
  const { control, removeOwner, saveNow } = props;
  return (
    <React.Fragment>
      {control.owner_list.length > 0 && (
        <span className="tags-container owners-container">
          {control.owner_list.map((owner, index) => {
            return <TagComponent key={index} tag={owner} onClick={removeOwner ? (evt) => removeOwner(owner, control, saveNow) : null} />;
          })}
        </span>
      )}
    </React.Fragment>
  );
}

function TagsViewComponent(props) {
  const { control, removeTag, saveNow } = props;
  return (
    <React.Fragment>
      {control.tag_list.length > 0 && (
        <span className="tags-container">
          {control.tag_list.map((tag, index) => {
            return <TagComponent key={index} tag={tag} onClick={removeTag ? (evt) => removeTag(tag, control, saveNow) : null} />;
          })}
        </span>
      )}
    </React.Fragment>
  );
}

function TagComponent(props) {
  const { key, tag, onClick } = props;
  return (
    <div key={key} className={"tag " + tag}>
      {tag}
      {onClick && <i aria-hidden="true" className="delete icon" onClick={onClick}></i>}
    </div>
  );
}

function TagsInputComponent(props) {
  const { control, updateTags, createNewTag, saveNow, doneable, onDone, defaultTagOptions } = props;

  return (
    <TagsDropdown
      className="library-control-tags-dropdown tags-dropdown"
      trigger={<span className="link text-light-blue">{getText("Tags")}</span>}
      header={`${getText("Assign tags")}:`}
      tagList={control.tag_list}
      onApply={(tag_list) => updateTags(tag_list, control, saveNow)}
      onDone={onDone}
      tagOptions={defaultTagOptions}
      onNewTag={createNewTag}
      addable
      searchable
      doneable={doneable}
    />
  );
}

const ControlAdministratorIcon = () => {
  return (
    <Popup
      trigger={ <sup><Icon name='control-administrator question circle'/></sup> }
      content={ <span>{getText("The control administrator is the person in charge of managing this particular control. This is different from risk owner or risk accountable manager.")}</span> }
      className = "control-administrator-popup"
      horizontalOffset={14}
    />
  )
}

function OwnersInputComponent(props) {
  const { control, updateOwners, createNewOwner, saveNow, doneable, onDone, defaultOwnerOptions } = props;
  const { enter_new_administrator, search_administrators } = props.translations;
  return (
    <TagsDropdown
      className="library-control-owners-dropdown tags-dropdown"
      trigger={<span className="link text-light-blue">{getText("Control Administrator")}{<ControlAdministratorIcon/>}</span>}
      header={`${getText("Assign control administrator")}:`}
      tagList={control.owner_list}
      onApply={(owner_list) => updateOwners(owner_list, control, saveNow)}
      onDone={onDone}
      tagOptions={defaultOwnerOptions}
      onNewTag={createNewOwner}
      addable={true}
      searchable={true}
      searchPlaceholder={search_administrators}
      newPlaceholder={enter_new_administrator}
      doneable={doneable}
    />
  );
}

class ControlLibraryComponents extends ControlLibraryActions {
  controlDetailsRenderer = (cell, row) => {
    const language = localStorage.getItem('language') || "english";
    const pendingTranslations = this.props.library_control.pendingTranslations
    const { isPendingTranslation, isFailedTranslation } = checkPendingTranslation(row, pendingTranslations, language);

    return (
      <LibraryControlComponent
        control={row}
        onClick={this.showUpdateControl}
        showTranslationModal={this.props.showTranslationModal}
        instanceConfig={this.props.instanceConfig}
        isPendingTranslation={isPendingTranslation}
        isFailedTranslation={isFailedTranslation}
      />
    );
  };

  timestampsRenderer = (cell, row) => {
    return (
      <div className="control-timestamps">
        <div>
          <label>{getText("Last Modified")}:</label> {moment(row.updated_at).format("DD-MMM-YYYY HH:mm A")}
        </div>
        <div>
          <label>{getText("Date Created")}:</label> {moment(row.created_at).format("DD-MMM-YYYY HH:mm A")}
        </div>
      </div>
    );
  };

  actionsRenderer = (cell, row) => {
    const onTagsDone = (tag_list) => this.updateTags(tag_list, row, true)
    const onOwnersDone = (owner_list) => this.updateOwners(owner_list, row, true)

    return (
      <div className="control-actions">
        <span className="link text-light-blue" onClick={() => this.showUpdateControl(row)}>
          {getText("Edit")}
        </span>
        <TagsInputComponent
          control={row}
          defaultTagOptions={this.state.defaultTagOptions}
          updateTags={this.updateTags}
          createNewTag={this.createNewTag}
          saveNow={false}
          onDone={onTagsDone}
          doneable
        />
        <OwnersInputComponent
          control={row}
          defaultOwnerOptions={this.state.defaultOwnerOptions}
          updateOwners={this.updateOwners}
          createNewOwner={this.createNewOwner}
          saveNow={false}
          translations={this.state.translations}
          onDone={onOwnersDone}
          doneable
        />
        {!row.archived && (
          <span className="link text-light-blue" onClick={() => this.showConfirmArchiveControl(row)}>
            {getText("Archive")}
          </span>
        )}
        {row.archived && (
          <span className="link text-light-blue" onClick={() => this.showConfirmUnarchiveControl(row)}>
            {getText("Unarchive")}
          </span>
        )}
        <span className="link text-light-blue" onClick={() => this.showControlUtilisation(row)}>
          {getText("Control Utilisation")} (<strong>{row.utilisation_count}</strong>)
        </span>
      </div>
    );
  };
}

export {
  LibraryControlComponent,
  ControlLibraryComponents,
  TagsAndOwnersViewComponent,
  TagsInputComponent,
  OwnersInputComponent,
  OwnersViewComponent,
  TagsViewComponent,
};



// WEBPACK FOOTER //
// ./src/views/control_library/control_library_components.js